import {
  BellRing,
  Building2,
  FileText,
  Files,
  Handshake,
  KeyRound,
  Landmark,
  LayoutDashboard,
  ListTree,
  LogOut,
  Package,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
  Wallet,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';


import { AlterarSenhaDialog } from './AlterarSenhaDialog';

import { useAuth, type UserRole } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';


interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  roles?: UserRole[];
}

const navItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/proposals', label: 'Propostas', icon: ScrollText },
  { to: '/invoices', label: 'Notas Fiscais', icon: FileText },
  { to: '/batch-billing', label: 'Faturar em Lote', icon: Zap, roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR'] },
  { to: '/clients', label: 'Clientes', icon: Building2 },
  { to: '/contracts', label: 'Contratos', icon: Files },
  { to: '/services', label: 'Catálogo (Serviços)', icon: Package, roles: ['SUPER_ADMIN', 'ADMIN'] },
  { to: '/certificates', label: 'Certificados', icon: ShieldCheck },
  { to: '/financial', label: 'Financeiro', icon: Wallet },
  { to: '/boletos', label: 'Boletos', icon: Receipt },
  { to: '/bank-accounts', label: 'Contas Bancárias', icon: Landmark },
  { to: '/reminders', label: 'Lembretes', icon: BellRing },
  { to: '/categories', label: 'Plano de Contas', icon: ListTree, roles: ['SUPER_ADMIN', 'ADMIN'] },
  { to: '/partners', label: 'Sócios', icon: Handshake, roles: ['SUPER_ADMIN', 'ADMIN'] },
  { to: '/users', label: 'Usuários', icon: Users, roles: ['SUPER_ADMIN', 'ADMIN'] },
  { to: '/issuer-companies', label: 'Empresas Emissoras', icon: Settings },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const [trocandoSenha, setTrocandoSenha] = useState(false);

  const visible = navItems.filter((item) => !item.roles || (user && item.roles.includes(user.role)));

  return (
    <div className="grid min-h-screen grid-cols-[240px_1fr] bg-background text-foreground">
      <aside className="flex flex-col bg-sidebar text-sidebar-foreground">
        <div className="flex h-16 items-center gap-2 px-4 font-semibold tracking-tight">
          <img src="/logo.png" alt="Simple Hotel" className="h-8 w-8 rounded-full" />
          <span className="leading-tight flex-1">
            <span className="block text-sm">Simple Administrativo</span>
            <span className="block text-[10px] font-normal text-sidebar-foreground/60">
              Simple Hotel · v0.1
            </span>
          </span>
        </div>

        <nav className="flex-1 px-3 py-2">
          <ul className="space-y-1">
            {visible.map(({ to, label, icon: Icon, end }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                      'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                      isActive &&
                        'bg-sidebar-accent text-sidebar-accent-foreground font-medium ring-1 ring-sidebar-primary/40',
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {user && (
          <div className="border-t border-sidebar-accent/40 p-3 space-y-2">
            <div className="px-2">
              <div className="text-sm font-medium truncate" data-dica={user.fullName}>
                {user.fullName}
              </div>
              <div className="text-xs text-sidebar-foreground/60 truncate" data-dica={user.email}>
                {user.email}
              </div>
              <div className="mt-1 inline-block rounded-full bg-sidebar-primary/20 px-2 py-0.5 text-[10px] uppercase tracking-wide text-sidebar-primary">
                {user.role}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setTrocandoSenha(true)}
              className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            >
              <KeyRound className="h-4 w-4" />
              Alterar senha
            </button>
            <button
              type="button"
              onClick={() => void logout()}
              className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            >
              <LogOut className="h-4 w-4" />
              Sair
            </button>
          </div>
        )}
      </aside>

      {trocandoSenha && <AlterarSenhaDialog onClose={() => setTrocandoSenha(false)} />}

      <main className="overflow-y-auto">
        <div className="container py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
